import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import React, { ChangeEvent, useState, useMemo } from "react";
import * as Yup from "yup";

import EmptyImage from "../../../../assets/accountSettings/stores/EmptyImage.svg";
import EmptyImageRed from "../../../../assets/accountSettings/stores/EmptyImageRed.svg";
import { ImageCropper } from "../../../../components/ImageCropper";
import { SaveButton } from "../../../../components/SaveButton";
import { Store, InputStore } from "../../../../types/stores";
//import { convertToBase64 } from "../../../../utils/base64";
import { formatCEP } from "../../../../utils/masks";
import {
  BoxCheckBox,
  ButtonConfig,
  FormControlFormPag,
  SectionOne,
  SectionTwoText,
  TextFieldForms,
  TypographySubTitle,
  TypographyTitle,
  useStyles,
} from "./styles";

interface IGlobalStoreProps {
  showModal: boolean;
  data: Store; // datas of the existing store
  onSave: (store: InputStore) => Promise<void>;
  onClose: () => void;
}

const EDIT_STORE_VALIDATION = Yup.object().shape({
  store_name: Yup.string().nullable(),
  whatsapp: Yup.string().nullable(),
  cep: Yup.string().nullable(),
  uf: Yup.string().max(2, "Só pode haver 2 caracteres").nullable(),
  address: Yup.string().nullable(),
  district: Yup.string().nullable(),
  address_number: Yup.string().nullable(),
  city: Yup.string().nullable(),
  base64: Yup.mixed().nullable(),
  // site information below
  receive_cash: Yup.boolean().nullable(),
  receive_debit: Yup.boolean().nullable(),
  receive_credit: Yup.boolean().nullable(),
  receive_billet: Yup.boolean().nullable(),
  receive_meal_ticket: Yup.boolean().nullable(),
  receive_pix: Yup.boolean().nullable(),
  // payment_form: Yup.mixed().nullable(),
  shipping: Yup.string().nullable(),
  service_tax: Yup.string().nullable(),
  max_items: Yup.number().min(0).nullable(),
  min_purchases: Yup.string().nullable(),
  description: Yup.string().nullable(),
  shipping_type: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.number(),
    })
    .nullable(),
  pix_key: Yup.string().nullable(),
  is_not_remove_background: Yup.boolean().nullable(),
});

const NEW_STORE_VALIDATION = Yup.object().shape({
  store_name: Yup.string().nullable(),
  whatsapp: Yup.string().nullable(),
  cep: Yup.string().nullable(),
  uf: Yup.string().max(2, "Só pode haver 2 caracteres").nullable(),
  address: Yup.string().nullable(),
  district: Yup.string().nullable(),
  address_number: Yup.string().nullable(),
  city: Yup.string().nullable(),
  base64: Yup.mixed().nullable(),
  // site information below
  receive_cash: Yup.boolean().nullable(),
  receive_debit: Yup.boolean().nullable(),
  receive_credit: Yup.boolean().nullable(),
  receive_billet: Yup.boolean().nullable(),
  receive_meal_ticket: Yup.boolean().nullable(),
  receive_pix: Yup.boolean().nullable(),
  // payment_form: Yup.mixed().nullable(),
  shipping: Yup.string().nullable(),
  service_tax: Yup.string().nullable(),
  max_items: Yup.number().min(0).nullable(),
  min_purchases: Yup.string().nullable(),
  description: Yup.string().nullable(),
  shipping_type: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.number(),
    })
    .nullable(),
  pix_key: Yup.string().nullable(),
  is_not_remove_background: Yup.boolean().nullable(),
});

export default function ModalGlobalStore({
  showModal,
  data,
  onSave,
  onClose,
}: IGlobalStoreProps): JSX.Element {
  const [base64, setBase64] = useState<string | null>(null);
  const [moreSettings, setMoreSettings] = useState(false);
  const [imageToCropOriginal, setImageToCropOriginal] = useState<
    string | ArrayBuffer | null
  >("");
  const [imageToCrop, setImageToCrop] = useState<string | ArrayBuffer | null>(
    ""
  );
  const [croppedImage, setCroppedImage] = useState<any>("");
  const styles = useStyles();
  const shippingTypes = [
    { label: "Delivery", value: 1 },
    { label: "Drive", value: 2 },
    { label: "Delivery/Drive", value: 3 },
  ];

  const INITIAL_FORMIK_DATA: InputStore = useMemo(() => {
    console.log("initial_formik - data", data);
    return {
      store_id: data.id_loja,
      store_name: data.nome_loja || "",
      whatsapp: data.whatsapp_phone_number || "",
      cep: data.cep || "",
      uf: data.uf || "",
      address: data.endereco || "",
      district: data.bairro || "",
      address_number: data.numero || "",
      city: data.cidade || "",
      base64: data.logo1 || "",
      receive_cash: data.aceita_dinheiro || false,
      receive_debit: data.aceita_debito || false,
      receive_credit: data.aceita_credito || false,
      receive_billet: data.aceita_boleto || false,
      receive_meal_ticket: data.aceita_vr || false,
      receive_pix: data.aceita_pix || false,
      shipping: data.frete?.toString() || undefined,
      service_tax: data.taxa_servico?.toString() || "",
      max_items: data.maximo_item || undefined,
      min_purchases: data.minimo_compra?.toString() || "",
      description: data.descricao || "",
      shipping_type: data.tipo_pedido || ({} as InputStore["shipping_type"]),
      pix_key: data.chave_pix || "",
      is_not_remove_background: true,
    };
  }, [data]);

  const handleEditStore = async (values: InputStore) => {
    try {
      await onSave(values);
      handleClose();
    } catch (err: any) {
      console.log(err);
    }
  };

  const formik = useFormik<InputStore>({
    initialValues: INITIAL_FORMIK_DATA,
    isInitialValid: false,
    validationSchema:
      INITIAL_FORMIK_DATA.store_id !== undefined
        ? EDIT_STORE_VALIDATION
        : NEW_STORE_VALIDATION,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: handleEditStore,
    enableReinitialize: true,
  });

  function handleClose() {
    formik.resetForm();
    setBase64(null);
    onClose();
    setImageToCrop("");
  }

  const onUploadFile = (event: any) => {
    if (event.target.files && event.target.files.length > 0) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        setImageToCrop(reader.result);
        setImageToCropOriginal(reader.result);
      });
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      data-testid="form-acc-settings-new-store"
    >
      <Box>
        <Dialog
          open={showModal}
          onClose={() => handleClose()}
          aria-labelledby="form-dialog-title"
          maxWidth={"md"}
          fullWidth
        >
          <DialogContent>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Box
                width={"50%"}
                height={"100%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bgcolor={
                    Boolean(formik.errors.base64) ? "#D54D4D0D" : "#F5f5f5"
                  }
                  border={
                    Boolean(formik.errors.base64) ? "1px solid #D54D4D" : ""
                  }
                  width="100%"
                  height="380px"
                >
                  {imageToCrop && (
                    <ImageCropper
                      imageToCrop={imageToCrop as string}
                      onImageCropped={(croppedImage) =>
                        setCroppedImage(croppedImage)
                      }
                    />
                  )}
                  {!imageToCrop && (
                    <img
                      data-testid="img-acc-settings-upload-logo-update"
                      src={
                        formik.values.base64
                          ? formik.values.base64
                          : base64
                          ? base64
                          : Boolean(formik.errors.base64)
                          ? EmptyImageRed
                          : EmptyImage
                      }
                      alt={"Caixa para inserir a logo da empresa a ser salva"}
                      style={{
                        // @ts-ignore:next-line
                        width: base64 ? "100%" : null,
                        // @ts-ignore:next-line
                        maxHeight: base64 ? "100%" : null,
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>
                <Box alignSelf="flex-start">
                  <input
                    data-testid="input-file-acc-settings-logo-update"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="contained-button-file"
                    type="file"
                    onChange={onUploadFile}
                  />
                  {!imageToCrop && (
                    <label htmlFor="contained-button-file">
                      <Button
                        variant="outlined"
                        component="span"
                        className={styles.buttonImportarImg}
                      >
                        Importar imagem
                        {base64 && <DoneAllIcon style={{ marginLeft: 10 }} />}
                      </Button>
                    </label>
                  )}

                  {imageToCrop && (
                    <Button
                      className={styles.buttonImg}
                      variant="outlined"
                      //className={classes.buttonImg}
                      onClick={() => {
                        formik.setFieldValue("base64", croppedImage);
                        setBase64(croppedImage);
                        // console.log('formikkkkk: ',formik)
                        setImageToCrop(null);
                        // setCroppedImage("");
                      }}
                    >
                      Cortar
                    </Button>
                  )}

                  {!!croppedImage && !imageToCrop && (
                    <Button
                      variant="outlined"
                      className={styles.buttonImg}
                      onClick={() => {
                        formik.setFieldValue("base64", imageToCropOriginal);
                        setCroppedImage(imageToCropOriginal);
                        setImageToCrop(imageToCropOriginal);
                      }}
                    >
                      Cortar Novamente
                    </Button>
                  )}

                  {base64 && (
                    <BoxCheckBox alignSelf="flex-start">
                      <Checkbox
                        data-testid="checkbox-acc-settings-remove-background-logo-update"
                        value={formik.values.is_not_remove_background}
                        onChange={({ target }) => {
                          formik.setFieldValue(
                            "is_not_remove_background",
                            !target.checked
                          );
                        }}
                        name="Remover fundo da imagem."
                      />{" "}
                      Remover fundo da imagem.
                    </BoxCheckBox>
                  )}
                </Box>
              </Box>
              <Box
                width={"60%"}
                marginLeft="32px"
                sx={{
                  opacity: !!imageToCrop ? 0.5 : 1,
                }}
              >
                <div>
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    lineHeight={3}
                    marginBottom={0}
                  >
                    Criação da loja
                  </Typography>
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    lineHeight={1.4}
                    marginBottom={3}
                    color="#616161"
                  >
                    Preencha as informações da sua loja.
                  </Typography>

                  <TextField
                    data-testid="txf-acc-settings-update-store-name"
                    id="txf-name"
                    name="name"
                    label="Nome"
                    placeholder="Nome"
                    variant="outlined"
                    value={formik.values.store_name}
                    error={Boolean(formik.errors.store_name)}
                    helperText={
                      Boolean(formik.errors.store_name) &&
                      formik.errors.store_name
                    }
                    onChange={({ target: { value } }) => {
                      formik.setFieldValue("store_name", value);
                    }}
                    sx={{
                      padding: "0px 0px 20px 0px",
                      width: "100%",
                    }}
                  />

                  <TextField
                    data-testid="txf-acc-settings-update-store-wpp"
                    id="outlined-basic"
                    label="Whatsapp"
                    placeholder="Whatsapp"
                    variant="outlined"
                    value={formik.values.whatsapp}
                    error={Boolean(formik.errors.whatsapp)}
                    helperText={
                      Boolean(formik.errors.whatsapp) && formik.errors.whatsapp
                    }
                    onChange={({ target: { value } }) => {
                      formik.setFieldValue("whatsapp", value);
                    }}
                    sx={{
                      padding: "0px 0px 20px 0px",
                      width: "100%",
                    }}
                  />

                  <Box
                    sx={{
                      padding: "0px 0px 20px 0px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <TextField
                      data-testid="txf-acc-settings-update-store-cep"
                      id="txf-cep"
                      name="cep"
                      label="CEP"
                      placeholder="CEP"
                      variant="outlined"
                      value={formik.values.cep}
                      error={Boolean(formik.errors.cep)}
                      helperText={
                        Boolean(formik.errors.cep) && formik.errors.cep
                      }
                      onChange={({ target: { value } }) => {
                        formik.setFieldValue("cep", formatCEP(value));
                      }}
                      sx={{
                        width: "48%",
                      }}
                    />
                    <TextField
                      data-testid="txf-acc-settings-update-store-uf"
                      id="txf-uf"
                      name="uf"
                      label="UF"
                      placeholder="UF"
                      variant="outlined"
                      value={formik.values.uf}
                      error={Boolean(formik.errors.uf)}
                      helperText={Boolean(formik.errors.uf) && formik.errors.uf}
                      onChange={({ target: { value } }) => {
                        formik.setFieldValue("uf", value);
                      }}
                      sx={{
                        width: "48%",
                      }}
                    />
                  </Box>

                  <TextField
                    data-testid="txf-acc-settings-update-store-address"
                    id="outlined-basic"
                    label="Endereço"
                    placeholder="Endereço"
                    variant="outlined"
                    value={formik.values.address}
                    error={Boolean(formik.errors.address)}
                    helperText={
                      Boolean(formik.errors.address) && formik.errors.address
                    }
                    onChange={({ target: { value } }) => {
                      formik.setFieldValue("address", value);
                    }}
                    sx={{
                      padding: "0px 0px 20px 0px",
                      width: "100%",
                    }}
                  />
                  <SectionTwoText>
                    <TextField
                      data-testid="txf-acc-settings-update-store-district"
                      id="outlined-basic"
                      label="Bairro"
                      placeholder="Bairro"
                      variant="outlined"
                      value={formik.values.district}
                      error={Boolean(formik.errors.district)}
                      helperText={
                        Boolean(formik.errors.district) &&
                        formik.errors.district
                      }
                      onChange={({ target: { value } }) => {
                        formik.setFieldValue("district", value);
                      }}
                    />
                    <TextField
                      data-testid="txf-acc-settings-update-store-address-number"
                      value={formik.values.address_number}
                      error={Boolean(formik.errors.address_number)}
                      helperText={
                        Boolean(formik.errors.address_number) &&
                        formik.errors.address_number
                      }
                      onChange={({ target: { value } }) => {
                        formik.setFieldValue("address_number", value);
                      }}
                      label="Número"
                      className="form-control"
                      autoComplete="none"
                    />
                  </SectionTwoText>
                  <TextField
                    data-testid="txf-acc-settings-update-store-city"
                    id="outlined-basic"
                    label="Cidade"
                    placeholder="Cidade"
                    variant="outlined"
                    value={formik.values.city}
                    error={Boolean(formik.errors.city)}
                    helperText={
                      Boolean(formik.errors.city) && formik.errors.city
                    }
                    onChange={({ target: { value } }) => {
                      formik.setFieldValue("city", value);
                    }}
                    sx={{
                      padding: "0px 0px 20px 0px",
                      width: "100%",
                      marginTop: "20px",
                    }}
                  />
                  {moreSettings && (
                    <SectionOne>
                      <Divider
                        style={{ marginBottom: "24px", color: "#CFCFCF" }}
                      />
                      <TypographyTitle sx={{ fontWeight: "500" }}>
                        Configurações de site
                      </TypographyTitle>

                      <TypographySubTitle>
                        Configure as informações de site
                      </TypographySubTitle>

                      {/* @ts-ignore:next-line */}
                      <FormControlFormPag color="primary" component="fieldset">
                        <FormLabel component="legend">
                          Formas de pagamento
                        </FormLabel>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive_cash"
                                name="receive_cash"
                                value={formik.values.receive_cash}
                                checked={formik.values.receive_cash}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_cash",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="Dinheiro"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive-debit"
                                name="receive_debit"
                                value={formik.values.receive_debit}
                                checked={formik.values.receive_debit}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_debit",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="Debito"
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive-credit"
                                name="receive_credit"
                                value={formik.values.receive_credit}
                                checked={formik.values.receive_credit}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_credit",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="Credito"
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive-billet"
                                name="receive_billet"
                                value={formik.values.receive_billet}
                                checked={formik.values.receive_billet}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_billet",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="Boleto"
                          />

                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive-meal-ticket"
                                name="receive_meal_ticket"
                                value={formik.values.receive_meal_ticket}
                                checked={formik.values.receive_meal_ticket}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_meal_ticket",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="VR"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                data-testid="cb-acc-settings-update-receive-pix"
                                name="receive_pix"
                                value={formik.values.receive_pix}
                                checked={formik.values.receive_pix}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  console.log(e);
                                  formik.setFieldValue(
                                    //@ts-ignore:next-line
                                    "receive_pix",
                                    e.target.checked
                                  );
                                }}
                              />
                            }
                            label="PIX"
                          />
                        </FormGroup>
                      </FormControlFormPag>

                      <Autocomplete
                        freeSolo={true}
                        options={shippingTypes}
                        //@ts-ignore:next-line
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => {
                          formik.setFieldValue("shipping_type", value);
                        }}
                        renderInput={(params) => (
                          <TextFieldForms
                            {...params}
                            data-testid="acomplete-acc-settings-update-shipping-type"
                            id="outlined-basic"
                            label="Tipo de pedido"
                            variant="outlined"
                            value={formik.values.shipping_type}
                          />
                        )}
                      />
                      <SectionTwoText sx={{ marginTop: "32px" }}>
                        <TextFieldForms
                          data-testid="txf-acc-settings-update-shipping"
                          id="outlined-basic"
                          label="Taxa de Frete"
                          variant="outlined"
                          value={formik.values.shipping}
                          onChange={({
                            target: { value },
                          }: {
                            target: { value: string };
                          }) => {
                            formik.setFieldValue("shipping", value);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R$
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextFieldForms
                          data-testid="txf-acc-settings-update-service-tax"
                          id="outlined-basic"
                          label="Taxa de serviço"
                          variant="outlined"
                          value={formik.values.service_tax}
                          onChange={({ target: { value } }) => {
                            formik.setFieldValue("service_tax", value);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R$
                              </InputAdornment>
                            ),
                          }}
                          //classes={{root:classes.formFields}}
                        />
                      </SectionTwoText>

                      <SectionTwoText sx={{ marginTop: "32px" }}>
                        <TextFieldForms
                          data-testid="txf-acc-settings-update-number"
                          type="number"
                          id="outlined-basic"
                          label="Quantidade máxima de itens"
                          variant="outlined"
                          value={formik.values.max_items}
                          onChange={({ target: { value } }) => {
                            formik.setFieldValue("max_items", value);
                          }}
                          error={Boolean(formik.errors.max_items)}
                          helperText={
                            Boolean(formik.errors.max_items) &&
                            formik.errors.max_items
                          }
                          InputProps={{
                            inputProps: { min: 0 },
                          }}
                          //classes={{root:classes.formFields}}
                        />

                        <TextFieldForms
                          data-testid="txf-acc-settings-update-min-purchase"
                          id="outlined-basic"
                          label="Valor mínimo de compra"
                          variant="outlined"
                          value={formik.values.min_purchases}
                          onChange={({ target: { value } }) => {
                            formik.setFieldValue("min_purchases", value);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R$
                              </InputAdornment>
                            ),
                          }}
                          //classes={{root:classes.formFields}}
                        />
                      </SectionTwoText>
                      <TextFieldForms
                        data-testid="txf-acc-settings-update-pix-kay"
                        id="outlined-basic"
                        label="Chave PIX"
                        variant="outlined"
                        value={formik.values.pix_key}
                        sx={{ marginTop: "32px" }}
                        onChange={({ target: { value } }) => {
                          formik.setFieldValue("pix_key", value);
                        }}

                        //classes={{root:classes.formFields}}
                      />

                      <TextField
                        data-testid="txf-acc-settings-update-description"
                        value={formik.values.description}
                        sx={{ marginTop: "32px", width: "100%" }}
                        onChange={({ target: { value } }) =>
                          formik.setFieldValue("description", value)
                        }
                        label="Descrição"
                        autoComplete="none"
                        multiline
                      />
                    </SectionOne>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0px 0px 20px 0px",
                      gap: "1rem",
                    }}
                  >
                    <Button
                      data-testid="btt-acc-settings-update-close-form"
                      onClick={() => handleClose()}
                      sx={{ color: "#616161" }}
                    >
                      Cancelar
                    </Button>

                    <SaveButton
                      titulo="Salvar"
                      isSubmitting={formik.isSubmitting}
                      onSave={() => formik.handleSubmit()}
                      disabled={!!imageToCrop}
                    />
                  </Box>

                  {!moreSettings && (
                    <ButtonConfig
                      data-testid="btt-acc-settings-update-submit-form"
                      onClick={() => {
                        setMoreSettings(!moreSettings);
                      }}
                      sx={{
                        textDecorationLine: "underline",
                        color: "#127FFF",
                        "&:hover": {
                          background: "#fff",
                          textDecorationLine: "underline",
                        },
                      }}
                    >
                      Ver mais configurações
                    </ButtonConfig>
                  )}
                  {moreSettings && (
                    <ButtonConfig
                      data-testid="btt-acc-settings-update-more-configuration"
                      onClick={() => {
                        setMoreSettings(!moreSettings);
                      }}
                      sx={{
                        textDecorationLine: "underline",
                        color: "#127FFF",
                        "&:hover": {
                          background: "#fff",
                          textDecorationLine: "underline",
                        },
                      }}
                    >
                      Ver menos
                    </ButtonConfig>
                  )}
                </div>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </form>
  );
}
